import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Scale, ChevronDown, ChevronRight, ShieldCheck, Link2, Mail, User, Store,
  Phone, AtSign, ImageOff, Send, Archive, RotateCcw, CheckCircle2, XCircle, ExternalLink, StickyNote, Save,
} from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const KIND_META = {
  1: { key: "partner", label: "Notifier le partenaire (B2B)", icon: Store },
  2: { key: "finder", label: "Notifier le trouveur", icon: User },
  3: { key: "rescue", label: "Envoyer le lien de secours", icon: Link2 },
  4: { key: "refusal", label: "Répondre — refus au propriétaire", icon: XCircle },
};

function Contact({ value, icon: Icon }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
      <Icon size={13} className="text-slate-400 shrink-0" />
      {value ? value : <span className="italic text-slate-400">Non renseigné</span>}
    </span>
  );
}

function SidePanel({ title, icon: Icon, name, email, phone, children }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4">
      <p className="text-xs font-bold uppercase tracking-wide text-findo-indigo flex items-center gap-1.5 mb-2">
        <Icon size={14} /> {title}
      </p>
      <p className="font-semibold text-slate-800">{name || <span className="italic text-slate-400 font-normal">Non renseigné</span>}</p>
      <div className="mt-1.5 flex flex-col gap-1">
        <Contact value={email} icon={AtSign} />
        <Contact value={phone} icon={Phone} />
      </div>
      {children}
    </div>
  );
}

function EmailModal({ open, onClose, claimId, kind, initial, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && initial) { setSubject(initial.subject || ""); setBody(initial.body || ""); }
  }, [open, initial]);

  async function send() {
    setBusy(true);
    try {
      const res = await api(`/admin/disputes/${claimId}/email`, {
        method: "POST", body: { kind, subject, body },
      });
      toast.success(`E-mail envoyé à ${res.recipient}`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  const meta = KIND_META[kind] || {};
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="dispute-email-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-findo-navy flex items-center gap-2">
            <Mail size={18} className="text-findo-indigo" /> {meta.label}
          </DialogTitle>
        </DialogHeader>
        {initial?.recipient
          ? <p className="text-sm text-slate-500 -mt-1">Destinataire : <b className="text-slate-700">{initial.recipient}</b></p>
          : <p className="text-sm text-red-500 -mt-1">Aucun destinataire renseigné pour ce type d'e-mail.</p>}
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold text-slate-500">Objet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="dispute-email-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Message</label>
            <Textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} className="mt-1 resize-none text-sm leading-relaxed" data-testid="dispute-email-body" />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy} className="rounded-full font-semibold text-slate-500" data-testid="dispute-email-cancel">Annuler</Button>
          <Button onClick={send} disabled={busy || !initial?.recipient} className="rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="dispute-email-send">
            {busy ? <><FindoSpinner size={16} className="mr-1.5" /> Envoi…</> : <><Send size={16} className="mr-1.5" /> Envoyer</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisputeCard({ row, expanded, onToggle, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rescueBusy, setRescueBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);
  const [mail, setMail] = useState({ open: false, kind: 1, initial: null });

  const load = async () => {
    setLoading(true);
    try { const d = await api(`/admin/disputes/${row.id}`); setDetail(d); setNotes(d.notes || ""); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { if (expanded && !detail) load(); }, [expanded]); // eslint-disable-line

  async function saveNotes() {
    setNotesBusy(true);
    try { await api(`/admin/disputes/${row.id}/notes`, { method: "PUT", body: { notes } }); toast.success("Note enregistrée."); }
    catch (e) { toast.error(e.message); } finally { setNotesBusy(false); }
  }

  async function generateRescue() {
    setRescueBusy(true);
    try {
      await api(`/admin/disputes/${row.id}/rescue-link`, { method: "POST" });
      const fresh = await api(`/admin/disputes/${row.id}`);
      setDetail(fresh);
      setMail({ open: true, kind: 3, initial: fresh.emails.rescue });
      toast.success("Lien de secours généré (valable 72h, usage unique).");
    } catch (e) { toast.error(e.message); } finally { setRescueBusy(false); }
  }

  function openMail(kind) {
    const map = { 1: "partner", 2: "finder", 4: "refusal" };
    setMail({ open: true, kind, initial: detail.emails[map[kind]] });
  }

  async function closeTicket() {
    try { await api(`/admin/disputes/${row.id}/close`, { method: "POST" }); toast.success("Ticket clôturé."); onChanged?.(); }
    catch (e) { toast.error(e.message); }
  }
  async function reopenTicket() {
    try { await api(`/admin/disputes/${row.id}/reopen`, { method: "POST" }); toast.success("Ticket rouvert."); onChanged?.(); }
    catch (e) { toast.error(e.message); }
  }

  const badgeCls = /Rejeté/.test(row.status_label) ? "bg-red-100 text-red-700"
    : /Escaladé/.test(row.status_label) ? "bg-amber-100 text-amber-700"
    : /Clôturé/.test(row.status_label) ? "bg-slate-200 text-slate-600" : "bg-findo-indigo/10 text-findo-indigo";

  return (
    <div className="rounded-2xl border border-border bg-white card-shadow overflow-hidden" data-testid="dispute-card">
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors" data-testid={`dispute-toggle-${row.id}`}>
        {expanded ? <ChevronDown size={18} className="text-slate-400 shrink-0" /> : <ChevronRight size={18} className="text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 truncate">{row.object}</p>
          <p className="text-xs text-slate-400">{row.created_at ? new Date(row.created_at).toLocaleString("fr-FR") : "—"} · {row.claimant_name}</p>
        </div>
        {row.rescue_sent && <Badge className="bg-emerald-100 text-emerald-700 hidden sm:inline-flex"><Link2 size={11} className="mr-1" /> Lien envoyé</Badge>}
        <Badge className={`shrink-0 ${badgeCls}`}>{row.status_label}</Badge>
      </button>

      {expanded && (
        <div className="border-t border-border p-4 sm:p-5 bg-slate-50/50" data-testid={`dispute-detail-${row.id}`}>
          {loading || !detail ? (
            <div className="py-8 grid place-items-center text-slate-400"><FindoSpinner size={22} /></div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <SidePanel title="Réclamant (propriétaire)" icon={User}
                  name={detail.claimant?.name} email={detail.claimant?.email} phone={detail.claimant?.phone}>
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Réponses fournies</p>
                    {(detail.answers || []).length ? detail.answers.map((a, i) => (
                      <div key={i} className="rounded-lg bg-slate-50 border border-border p-2.5">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{a.question || `Caractéristique ${i + 1}`}</p>
                        <p className="text-sm text-slate-700 font-medium">{a.answer}</p>
                      </div>
                    )) : <p className="text-sm text-slate-400 italic">Aucune réponse fournie.</p>}
                  </div>
                </SidePanel>

                <SidePanel title={detail.counterpart?.label || "Contrepartie"}
                  icon={detail.counterpart?.kind === "partner" ? Store : User}
                  name={detail.counterpart?.name} email={detail.counterpart?.email} phone={detail.counterpart?.phone}>
                  <div className="mt-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Objet · Code {detail.code}</p>
                    <p className="text-sm text-slate-700 font-medium">{detail.object}</p>
                    {detail.reject_reason && <p className="mt-2 text-xs text-red-600">Motif du rejet : {detail.reject_reason}</p>}
                  </div>
                </SidePanel>
              </div>

              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Photos</p>
                {detail.photos?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {detail.photos.map((p, i) => (
                      <img key={i} src={p} alt="" className="h-24 w-24 object-cover rounded-xl border border-border" data-testid={`dispute-photo-${i}`} />
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-400 italic flex items-center gap-1.5"><ImageOff size={14} /> Aucune photo disponible.</p>}
              </div>

              {detail.rescue_url && (
                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-800" data-testid="dispute-rescue-url">
                  <ShieldCheck size={16} className="shrink-0" />
                  <span className="truncate flex-1">Lien de secours actif : <b className="break-all">{detail.rescue_url}</b></span>
                  <a href={detail.rescue_url} target="_blank" rel="noreferrer" className="shrink-0"><ExternalLink size={15} /></a>
                </div>
              )}

              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1.5">
                  <StickyNote size={13} /> Notes internes (superadmin uniquement)
                </p>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Tracez ici les décisions d'arbitrage, vérifications effectuées, échanges…"
                  className="resize-none text-sm bg-amber-50/40 border-amber-200" data-testid="dispute-notes-input" />
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="outline" onClick={saveNotes} disabled={notesBusy} className="rounded-full font-semibold" data-testid="dispute-notes-save">
                    {notesBusy ? <FindoSpinner size={14} className="mr-1.5" /> : <Save size={14} className="mr-1.5" />} Enregistrer la note
                  </Button>
                </div>
              </div>

              {!detail.closed && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={generateRescue} disabled={rescueBusy} className="rounded-full font-semibold bg-findo-indigo hover:bg-findo-indigo/90" data-testid="dispute-rescue-btn">
                    {rescueBusy ? <FindoSpinner size={15} className="mr-1.5" /> : <Link2 size={15} className="mr-1.5" />} Envoyer le lien de réclamation de secours
                  </Button>
                  {detail.counterpart?.kind === "partner" && (
                    <Button variant="outline" onClick={() => openMail(1)} className="rounded-full font-semibold" data-testid="dispute-mail-1"><Store size={15} className="mr-1.5" /> Mail partenaire</Button>
                  )}
                  {(detail.counterpart?.kind === "finder" || detail.emails?.finder?.recipient) && (
                    <Button variant="outline" onClick={() => openMail(2)} className="rounded-full font-semibold" data-testid="dispute-mail-2"><User size={15} className="mr-1.5" /> Mail trouveur</Button>
                  )}
                  <Button variant="outline" onClick={() => openMail(4)} className="rounded-full font-semibold border-red-200 text-red-600 hover:bg-red-50" data-testid="dispute-mail-4"><XCircle size={15} className="mr-1.5" /> Mail de refus</Button>
                  <Button variant="ghost" onClick={closeTicket} className="rounded-full font-semibold text-slate-500 ml-auto" data-testid="dispute-close-btn"><Archive size={15} className="mr-1.5" /> Clôturer</Button>
                </div>
              )}
              {detail.closed && (
                <div className="mt-4 flex items-center gap-2">
                  <Badge className="bg-slate-200 text-slate-600"><CheckCircle2 size={12} className="mr-1" /> {row.status_label}</Badge>
                  <Button variant="outline" onClick={reopenTicket} className="rounded-full font-semibold ml-auto" data-testid="dispute-reopen-btn"><RotateCcw size={15} className="mr-1.5" /> Rouvrir</Button>
                </div>
              )}

              <EmailModal open={mail.open} onClose={() => setMail((m) => ({ ...m, open: false }))}
                claimId={row.id} kind={mail.kind} initial={mail.initial} onSent={onChanged} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function DisputesPanel() {
  const [tab, setTab] = useState("open");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api(`/admin/disputes?status=${tab}`).then((r) => setRows(r.disputes || [])).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); setExpanded(null); }, [tab]); // eslint-disable-line

  return (
    <div data-testid="disputes-panel">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display font-bold text-lg text-findo-navy flex items-center gap-2"><Scale size={20} className="text-findo-indigo" /> Litiges &amp; réclamations de secours</h2>
      </div>
      <div className="inline-flex rounded-full border border-border bg-white p-1 mb-4">
        {[["open", "En cours"], ["closed", "Historique"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-findo-indigo text-white" : "text-slate-500 hover:text-findo-navy"}`}
            data-testid={`disputes-tab-${k}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center text-slate-400"><FindoSpinner size={24} /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-slate-400 text-sm" data-testid="disputes-empty">
          {tab === "open" ? "Aucun litige en cours." : "Aucun ticket clôturé."}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <DisputeCard key={r.id} row={r} expanded={expanded === r.id}
              onToggle={() => setExpanded((id) => (id === r.id ? null : r.id))} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}
